import { useCallback, useEffect, useRef, useState } from "react";
import {
  Activity, ShieldAlert, Search, ChevronDown, Loader2, Copy, Check, Eye, X
} from "lucide-react";
import { api, ApiError, type AdminLogEntry } from "../../lib/api";
import { AdminEmptyState, AdminFilterBar, AdminPageHeader, AdminTabBar, CategoryBadge, SeverityBadge } from "../../components/admin/AdminUI";
import { Modal } from "../../components/ui/Modal";
import { useAdminToken, PageLoader, PageError } from "./adminShared";

export function AdminLogs() {
  const token = useAdminToken();
  const [activeTab, setActiveTab] = useState<"activity" | "errors">("activity");

  // Operational Activity Logs state
  const [actCategory, setActCategory] = useState("all");
  const [actSeverity, setActSeverity] = useState("all");
  const [actSearch, setActSearch] = useState("");
  const [debouncedActSearch, setDebouncedActSearch] = useState("");
  const [actItems, setActItems] = useState<AdminLogEntry[]>([]);
  const [actTotal, setActTotal] = useState(0);
  const [actLoading, setActLoading] = useState(true);

  // Diagnostics & Error Logs state
  const [page, setPage] = useState(1);
  const [source, setSource] = useState("all");
  const [level, setLevel] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [userQuery, setUserQuery] = useState("");
  const [requestId, setRequestId] = useState("");

  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
  const [detail, setDetail] = useState<any | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedActSearch(actSearch), 300);
    return () => clearTimeout(t);
  }, [actSearch]);

  const loadActivity = useCallback(() => {
    if (!token) return;
    setActLoading(true);
    api.admin
      .logs(token, {
        category: actCategory !== "all" ? actCategory : undefined,
        severity: actSeverity !== "all" ? actSeverity : undefined,
        search: debouncedActSearch.trim() || undefined,
        limit: 150,
      })
      .then((data) => {
        setActItems(data.items || []);
        setActTotal(data.total || (data.items || []).length);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : "Failed to load activity logs"))
      .finally(() => setActLoading(false));
  }, [token, actCategory, actSeverity, debouncedActSearch]);

  const loadErrors = useCallback(() => {
    if (!token) return;
    setLoading(true);
    api.admin
      .errorLogs(token, {
        page,
        limit: 25,
        source: source !== "all" ? source : undefined,
        level: level !== "all" ? level : undefined,
        q: debouncedSearch.trim() || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        user_id: userQuery.trim() || undefined,
        request_id: requestId.trim() || undefined,
      })
      .then((data) => {
        setItems(data.items);
        setTotal(data.total);
        setTotalPages(data.totalPages);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : "Failed to load error logs"))
      .finally(() => setLoading(false));
  }, [token, page, source, level, debouncedSearch, startDate, endDate, userQuery, requestId]);

  useEffect(() => {
    if (activeTab === "activity") {
      loadActivity();
    } else {
      loadErrors();
    }
  }, [activeTab, loadActivity, loadErrors]);

  useEffect(() => {
    if (!token || !selectedLogId) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    api.admin
      .errorLogDetail(token, selectedLogId)
      .then(setDetail)
      .catch((e) => setError(e instanceof ApiError ? e.message : "Failed to load log detail"))
      .finally(() => setDetailLoading(false));
  }, [token, selectedLogId]);

  const copyText = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const hasFilters = Boolean(
    source !== "all" || level !== "all" || search || startDate || endDate || userQuery || requestId
  );

  const clearFilters = () => {
    setSource("all");
    setLevel("all");
    setSearch("");
    setStartDate("");
    setEndDate("");
    setUserQuery("");
    setRequestId("");
    setPage(1);
  };

  return (
    <div className="p-8 space-y-6">
      <AdminPageHeader
        title="System & Activity Logs"
        subtitle="Live monitoring for critical operations (auth, payments, uploads, extractions) and diagnostic exceptions"
      />

      {/* Mode Switcher Tabs */}
      <div className="flex items-center gap-2 border-b border-border pb-3">
        <button
          type="button"
          onClick={() => setActiveTab("activity")}
          className={`px-4 py-2 rounded-lg text-xs font-semibold transition ${
            activeTab === "activity"
              ? "bg-primary text-primary-foreground shadow-xs"
              : "bg-muted text-muted-foreground hover:text-foreground"
          }`}
        >
          Activity &amp; Operational Logs ({actTotal})
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("errors")}
          className={`px-4 py-2 rounded-lg text-xs font-semibold transition ${
            activeTab === "errors"
              ? "bg-primary text-primary-foreground shadow-xs"
              : "bg-muted text-muted-foreground hover:text-foreground"
          }`}
        >
          Diagnostics &amp; Error Logs ({total})
        </button>
      </div>

      {error && (
        <div className="p-3.5 bg-destructive/10 border border-destructive/20 rounded-xl text-destructive text-sm flex items-center justify-between">
          <span>{error}</span>
          <button type="button" onClick={() => setError("")}><X size={14} /></button>
        </div>
      )}

      {activeTab === "activity" ? (
        <>
          {/* Operational Activity Toolbar */}
          <div className="bg-card rounded-lg border border-border p-4 shadow-sm space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <label className="text-xs space-y-1 block">
                <span className="font-semibold text-muted-foreground uppercase text-xs">Category</span>
                <select
                  value={actCategory}
                  onChange={(e) => setActCategory(e.target.value)}
                  className="w-full rounded-lg border border-border px-2.5 py-1.5 text-xs bg-background"
                >
                  <option value="all">All categories</option>
                  <option value="auth">Auth &amp; Access</option>
                  <option value="payments">Payments &amp; Billing</option>
                  <option value="resources">Resource Uploads</option>
                  <option value="extractions">Extraction Requests</option>
                  <option value="ai">AI &amp; Features</option>
                  <option value="users">Users &amp; Students</option>
                  <option value="admins">Admin Audits</option>
                  <option value="error">Errors &amp; Crucial</option>
                </select>
              </label>

              <label className="text-xs space-y-1 block">
                <span className="font-semibold text-muted-foreground uppercase text-xs">Severity</span>
                <select
                  value={actSeverity}
                  onChange={(e) => setActSeverity(e.target.value)}
                  className="w-full rounded-lg border border-border px-2.5 py-1.5 text-xs bg-background"
                >
                  <option value="all">All severities</option>
                  <option value="info">Info</option>
                  <option value="success">Success</option>
                  <option value="warning">Warning</option>
                  <option value="error">Error</option>
                </select>
              </label>

              <label className="text-xs space-y-1 block">
                <span className="font-semibold text-muted-foreground uppercase text-xs">Search Events</span>
                <input
                  type="text"
                  placeholder="Filter by title, detail, actor, phone…"
                  value={actSearch}
                  onChange={(e) => setActSearch(e.target.value)}
                  className="w-full rounded-lg border border-border px-2.5 py-1.5 text-xs bg-background"
                />
              </label>
            </div>
          </div>

          {/* Activity Table */}
          {actLoading ? (
            <PageLoader />
          ) : (
            <div className="bg-card rounded-lg border border-border overflow-hidden shadow-sm">
              <div className="overflow-x-auto admin-scroll-x">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="border-b border-border bg-muted/40 font-semibold text-muted-foreground">
                      <th className="py-3 px-4">Time</th>
                      <th className="py-3 px-3">Category</th>
                      <th className="py-3 px-3">Severity</th>
                      <th className="py-3 px-4">Event / Description</th>
                      <th className="py-3 px-3">Actor / User</th>
                      <th className="py-3 px-3">Action / Endpoint</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {actItems.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-12 text-center text-muted-foreground">
                          No activity events matching your filters
                        </td>
                      </tr>
                    ) : (
                      actItems.map((entry, idx) => (
                        <tr key={entry.id || idx} className="hover:bg-muted/50 transition">
                          <td className="py-3 px-4 font-mono text-xs whitespace-nowrap text-muted-foreground">
                            {entry.time || entry.createdAt?.slice(0, 19)}
                          </td>
                          <td className="py-3 px-3 whitespace-nowrap">
                            <span className="inline-block px-2 py-0.5 rounded text-xs font-bold uppercase bg-secondary text-primary border border-border">
                              {entry.category}
                            </span>
                          </td>
                          <td className="py-3 px-3 whitespace-nowrap">
                            <span
                              className={`inline-block px-2 py-0.5 rounded text-xs font-bold uppercase ${
                                entry.severity === "success"
                                  ? "bg-success/10 text-success border border-success/30"
                                  : entry.severity === "error" || entry.severity === "crucial"
                                  ? "bg-destructive/15 text-destructive border border-destructive/30"
                                  : entry.severity === "warning"
                                  ? "bg-warning/10 text-warning border border-warning/30"
                                  : "bg-info/10 text-info border border-info/30"
                              }`}
                            >
                              {entry.severity}
                            </span>
                          </td>
                          <td className="py-3 px-4 max-w-md">
                            <p className="font-semibold text-foreground truncate">{entry.title}</p>
                            <p className="text-muted-foreground text-xs truncate">{entry.detail}</p>
                          </td>
                          <td className="py-3 px-3 whitespace-nowrap">
                            <span className="font-medium text-foreground block">{entry.actor || "—"}</span>
                            {entry.userPhone && (
                              <span className="text-xs text-muted-foreground font-mono">{entry.userPhone}</span>
                            )}
                          </td>
                          <td className="py-3 px-3 font-mono text-xs text-muted-foreground truncate max-w-xs">
                            {entry.endpoint || entry.entityType || "—"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {/* Error Diagnostics Filter Toolbar */}
          <div className="bg-card rounded-lg border border-border p-4 shadow-sm space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <label className="text-xs space-y-1 block">
                <span className="font-semibold text-muted-foreground uppercase text-xs">Source</span>
                <select
                  value={source}
                  onChange={(e) => { setSource(e.target.value); setPage(1); }}
                  className="w-full rounded-lg border border-border px-2.5 py-1.5 text-xs bg-background"
                >
                  <option value="all">All sources</option>
                  <option value="api">API</option>
                  <option value="web">Web</option>
                  <option value="bot">Bot</option>
                  <option value="job">Job</option>
                  <option value="payment">Payment</option>
                  <option value="ai">AI</option>
                </select>
              </label>

              <label className="text-xs space-y-1 block">
                <span className="font-semibold text-muted-foreground uppercase text-xs">Level</span>
                <select
                  value={level}
                  onChange={(e) => { setLevel(e.target.value); setPage(1); }}
                  className="w-full rounded-lg border border-border px-2.5 py-1.5 text-xs bg-background"
                >
                  <option value="all">All levels</option>
                  <option value="error">Error</option>
                  <option value="warning">Warning</option>
                </select>
              </label>

              <label className="text-xs space-y-1 block">
                <span className="font-semibold text-muted-foreground uppercase text-xs">Text search</span>
                <input
                  type="text"
                  placeholder="Search title, msg, path…"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className="w-full rounded-lg border border-border px-2.5 py-1.5 text-xs bg-background"
                />
              </label>

              <label className="text-xs space-y-1 block">
                <span className="font-semibold text-muted-foreground uppercase text-xs">Request ID</span>
                <input
                  type="text"
                  placeholder="req_..."
                  value={requestId}
                  onChange={(e) => { setRequestId(e.target.value); setPage(1); }}
                  className="w-full rounded-lg border border-border px-2.5 py-1.5 text-xs bg-background font-mono"
                />
              </label>

              <label className="text-xs space-y-1 block">
                <span className="font-semibold text-muted-foreground uppercase text-xs">Start Date</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                  className="w-full rounded-lg border border-border px-2.5 py-1.5 text-xs bg-background"
                />
              </label>

              <label className="text-xs space-y-1 block">
                <span className="font-semibold text-muted-foreground uppercase text-xs">End Date</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                  className="w-full rounded-lg border border-border px-2.5 py-1.5 text-xs bg-background"
                />
              </label>
            </div>

            <div className="flex items-center justify-between pt-1">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Filter by user UUID..."
                  value={userQuery}
                  onChange={(e) => { setUserQuery(e.target.value); setPage(1); }}
                  className="rounded-lg border border-border px-2.5 py-1 text-xs bg-background w-64 font-mono"
                />
                {hasFilters && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="text-xs text-muted-foreground hover:text-foreground underline font-medium"
                  >
                    Reset filters
                  </button>
                )}
              </div>
              <span className="text-xs text-muted-foreground">
                Total logs: <strong className="text-foreground">{total}</strong>
              </span>
            </div>
          </div>

          {/* Error Diagnostics Table */}
          {loading ? (
            <PageLoader />
          ) : (
            <div className="bg-card rounded-lg border border-border overflow-hidden shadow-sm">
              <div className="overflow-x-auto admin-scroll-x">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="border-b border-border bg-muted/40 font-semibold text-muted-foreground">
                      <th className="py-3 px-4">Time</th>
                      <th className="py-3 px-3">Level</th>
                      <th className="py-3 px-3">Source</th>
                      <th className="py-3 px-4">Title / Message</th>
                      <th className="py-3 px-3">User</th>
                      <th className="py-3 px-3">Route / Path</th>
                      <th className="py-3 px-3 text-right">Occurrences</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {items.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-12 text-center text-muted-foreground">
                          No error logs matching your filters
                        </td>
                      </tr>
                    ) : (
                      items.map((log) => (
                        <tr
                          key={log.id}
                          onClick={() => setSelectedLogId(log.id)}
                          className={`cursor-pointer transition hover:bg-muted/50 ${
                            log.level === "error" ? "bg-destructive/[0.02]" : ""
                          }`}
                        >
                          <td className="py-3 px-4 font-mono text-xs whitespace-nowrap text-muted-foreground">
                            {log.createdAtAddis || log.createdAt?.slice(0, 19)}
                          </td>
                          <td className="py-3 px-3 whitespace-nowrap">
                            <span
                              className={`inline-block px-2 py-0.5 rounded text-xs font-bold uppercase ${
                                log.level === "error"
                                  ? "bg-destructive/15 text-destructive border border-destructive/30"
                                  : "bg-warning/10 text-warning border border-warning/30"
                              }`}
                            >
                              {log.level}
                            </span>
                          </td>
                          <td className="py-3 px-3 whitespace-nowrap font-mono text-xs uppercase tracking-wide">
                            {log.source}
                          </td>
                          <td className="py-3 px-4 max-w-sm">
                            <p className="font-semibold text-foreground truncate">{log.title}</p>
                            <p className="text-muted-foreground text-xs truncate">{log.message}</p>
                          </td>
                          <td className="py-3 px-3 whitespace-nowrap">
                            {log.user ? (
                              <div>
                                <span className="font-medium text-foreground block">{log.user.name}</span>
                                <span className="text-xs text-muted-foreground">{log.user.phone || log.user.role}</span>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="py-3 px-3 max-w-[180px] font-mono text-xs truncate text-muted-foreground">
                            {log.method && <strong className="text-foreground mr-1">{log.method}</strong>}
                            {log.route || log.path || "—"}
                          </td>
                          <td className="py-3 px-3 text-right whitespace-nowrap">
                            <span className="inline-block px-2 py-0.5 rounded-full bg-secondary text-primary font-bold text-xs border border-border">
                              {log.occurrencesCount}×
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination Footer */}
              <div className="p-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground bg-muted/20">
                <span>
                  Page <strong className="text-foreground">{page}</strong> of <strong className="text-foreground">{totalPages}</strong> (25 per page)
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="px-3 py-1.5 rounded-md border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed font-medium"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className="px-3 py-1.5 rounded-md border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed font-medium"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Log Details Modal */}
      <Modal
        isOpen={!!selectedLogId}
        onClose={() => setSelectedLogId(null)}
        title="Error Log Details"
        description={`ID: ${selectedLogId}`}
        size="lg"
      >
        <div className="space-y-5 text-xs">

            <div className="p-6 overflow-y-auto space-y-5 text-xs flex-1">
              {detailLoading || !detail ? (
                <div className="py-16 text-center text-muted-foreground">Loading log details…</div>
              ) : (
                <>
                  {/* Summary Header */}
                  <div className="p-4 rounded-xl bg-destructive/5 border border-destructive/20 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="px-2 py-0.5 rounded text-xs font-bold uppercase bg-destructive/20 text-destructive">
                        {detail.level}
                      </span>
                      <span className="px-2 py-0.5 rounded text-xs font-bold uppercase bg-secondary text-primary border border-border">
                        {detail.source}
                      </span>
                      {detail.exceptionType && (
                        <span className="px-2 py-0.5 rounded text-xs font-mono font-semibold bg-muted border border-border">
                          {detail.exceptionType}
                        </span>
                      )}
                      <span className="ml-auto font-mono text-xs text-muted-foreground">
                        Addis: {detail.createdAtAddis} · UTC: {detail.createdAt}
                      </span>
                    </div>
                    <h4 className="font-bold text-sm text-foreground">{detail.title}</h4>
                    <p className="text-xs text-muted-foreground whitespace-pre-wrap">{detail.message}</p>
                  </div>

                  {/* Who & What Grid */}
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* WHO */}
                    <div className="p-4 rounded-xl border border-border bg-card space-y-2.5">
                      <h5 className="font-bold text-xs uppercase tracking-wider text-muted-foreground">
                        Who (User & Origin)
                      </h5>
                      <div className="space-y-1.5">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Name / Role:</span>
                          <span className="font-medium text-foreground">{detail.who.name || "Anonymous"} {detail.who.role ? `(${detail.who.role})` : ""}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">User ID:</span>
                          <span className="font-mono text-xs text-foreground">{detail.who.id || "—"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Telegram ID:</span>
                          <span className="font-mono text-foreground">{detail.who.telegramId || "—"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">IP Address:</span>
                          <span className="font-mono text-foreground">{detail.who.ip || "—"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">User Agent:</span>
                          <span className="text-muted-foreground truncate max-w-[200px]" title={detail.who.userAgent}>
                            {detail.who.userAgent || "—"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* WHAT */}
                    <div className="p-4 rounded-xl border border-border bg-card space-y-2.5">
                      <h5 className="font-bold text-xs uppercase tracking-wider text-muted-foreground">
                        What (Request Context)
                      </h5>
                      <div className="space-y-1.5">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Method & Status:</span>
                          <span className="font-semibold text-foreground">
                            {detail.what.method || "—"} · HTTP {detail.what.statusCode || "—"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Request ID:</span>
                          <span className="font-mono text-xs text-foreground">{detail.what.requestId || "—"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Duration:</span>
                          <span className="font-mono text-foreground">
                            {detail.what.durationMs != null ? `${detail.what.durationMs} ms` : "—"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Path / Route:</span>
                          <span className="font-mono text-xs truncate max-w-[200px] text-foreground" title={detail.what.path}>
                            {detail.what.path || "—"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sanitized Context / Payload */}
                  {detail.what.context && Object.keys(detail.what.context).length > 0 && (
                    <div className="space-y-1.5">
                      <h5 className="font-bold text-xs uppercase tracking-wider text-muted-foreground">
                        Sanitized Context & Payload
                      </h5>
                      <pre className="p-3 bg-muted rounded-xl text-xs font-mono overflow-x-auto border border-border max-h-40">
                        {JSON.stringify(detail.what.context, null, 2)}
                      </pre>
                    </div>
                  )}

                  {/* Stack Trace */}
                  {detail.stackTrace && (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <h5 className="font-bold text-xs uppercase tracking-wider text-muted-foreground">
                          Stack Trace
                        </h5>
                        <button
                          type="button"
                          onClick={() => copyText(detail.stackTrace)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded bg-muted hover:bg-muted/80 text-xs font-medium border border-border"
                        >
                          {copied ? <Check size={11} className="text-success" /> : <Copy size={11} />}
                          {copied ? "Copied" : "Copy trace"}
                        </button>
                      </div>
                      <pre className="p-3 bg-muted/80 rounded-xl text-xs font-mono overflow-auto border border-border max-h-56 leading-relaxed whitespace-pre">
                        {detail.stackTrace}
                      </pre>
                    </div>
                  )}

                  {/* Occurrences of Fingerprint */}
                  <div className="space-y-2.5 border-t border-border pt-4">
                    <div className="flex items-center justify-between">
                      <h5 className="font-bold text-xs uppercase tracking-wider text-muted-foreground">
                        Fingerprint Occurrences: <span className="font-mono text-primary font-bold">{detail.occurrences.count} total</span>
                      </h5>
                      <span className="text-xs text-muted-foreground font-mono truncate max-w-xs">
                        Hash: {detail.fingerprint.slice(0, 16)}…
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground flex gap-4">
                      <span>First seen: <strong className="text-foreground">{detail.occurrences.firstSeenAddis}</strong></span>
                      <span>Last seen: <strong className="text-foreground">{detail.occurrences.lastSeenAddis}</strong></span>
                    </div>

                    {detail.occurrences.latest?.length > 1 && (
                      <div className="space-y-1 mt-2">
                        <span className="text-xs font-semibold text-muted-foreground uppercase">Recent occurrences</span>
                        <div className="divide-y divide-border border border-border rounded-lg overflow-hidden bg-muted/20">
                          {detail.occurrences.latest.map((item: any) => (
                            <button
                              key={item.id}
                              type="button"
                              onClick={() => setSelectedLogId(item.id)}
                              className="w-full text-left px-3 py-2 text-xs flex items-center justify-between hover:bg-muted/50 transition font-mono"
                            >
                              <span>{item.createdAtAddis || item.createdAt} · {item.path || "—"}</span>
                              <span className="text-xs text-primary underline">Open log →</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="pt-4 border-t border-border flex justify-end">
              <button
                type="button"
                onClick={() => setSelectedLogId(null)}
                className="px-4 py-2 rounded-lg border border-border text-xs font-semibold hover:bg-muted cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
      </Modal>
    </div>
  );
}
